import { getLoyaltyUpgrade } from '~/utils/loyaltyUpgrade';

/**
 * Payment types svc-order records when a Société benefit is redeemed.
 * Room and package upgrades are also stored on the item, so the list can show
 * those without waiting for payments.
 */
const SOCIETE_BENEFIT_PAYMENT_TYPES: ReadonlySet<string> = new Set([
  'le_points_airport_transfer',
  'le_points_hotel_upgrade',
  'le_points_lounge_pass',
  'le_points_tour_optional_extra_voucher',
  'le_points_travelshoot_voucher',
  'le_points_discounted_insurance',
]);

const LOYALTY_TIER_THEME_KEYS = ['bronze', 'silver', 'gold', 'platinum', 'infinite'] as const;

export type LoyaltyTierThemeKey = (typeof LOYALTY_TIER_THEME_KEYS)[number];

type UpgradeItem = Parameters<typeof getLoyaltyUpgrade>[0];

export interface SocieteBenefitOrder {
  items?: Array<UpgradeItem>;
  bedbank_items?: Array<UpgradeItem>;
}

export interface SocieteBenefitPayment {
  type?: string;
  status?: string;
  intent?: string;
}

export function toLoyaltyTierThemeKey(tier: string | null | undefined): LoyaltyTierThemeKey | undefined {
  const normalized = tier?.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  return (LOYALTY_TIER_THEME_KEYS as ReadonlyArray<string>).includes(normalized)
    ? (normalized as LoyaltyTierThemeKey)
    : undefined;
}

/** True when a room or package upgrade is still recorded on a non-cancelled item. */
export function orderHasActiveLoyaltyUpgrade(order: SocieteBenefitOrder | null | undefined): boolean {
  if (!order) {
    return false;
  }

  const items = [...(order.items ?? []), ...(order.bedbank_items ?? [])];
  return items.some((item) => {
    const upgrade = getLoyaltyUpgrade(item);
    return !!upgrade && !upgrade.isCancelled;
  });
}

export function paymentRecordsSocieteBenefit(payment: SocieteBenefitPayment): boolean {
  if (!payment.type || !SOCIETE_BENEFIT_PAYMENT_TYPES.has(payment.type)) {
    return false;
  }
  if (payment.status === 'failed') {
    return false;
  }
  if (payment.intent && payment.intent !== 'sale') {
    return false;
  }
  return true;
}

export function paymentsIncludeSocieteBenefit(payments: Array<SocieteBenefitPayment> | null | undefined): boolean {
  return !!payments?.some(paymentRecordsSocieteBenefit);
}
